/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef, type ForwardRefExoticComponent, type RefAttributes } from 'react';

import { css, jsx } from '@compiled/react';

import { type StrictXCSSProp, type XCSSAllProperties, type XCSSAllPseudos } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { type BoxProps } from '@atlaskit/primitives/compiled';

export type TRProps = Exclude<BoxProps<'tr'>, 'backgroundColor' | 'xcss'> & {
	/**
	 * If the row has programatic selection applied.
	 */
	isSelected?: boolean;
	/**
	 * controlled styles to apply to the cell.
	 */
	xcss?: StrictXCSSProp<XCSSAllProperties, XCSSAllPseudos>;
};

const lastRowStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&:last-of-type:not(style)': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
		'& td:not(style)': {
			// Setting a CSS custom property lets TR change the border color of its td children
			// without needing any JS-level "isLastRow" knowledge.
			borderBlockEnd: 'none',
		},
	},
});

/**
 * __Row__
 *
 * A row primitive.
 *
 * - [Examples](https://atlassian.design/components/table/examples)
 */
export const TR: ForwardRefExoticComponent<Omit<TRProps, "ref"> & RefAttributes<HTMLTableRowElement>> = forwardRef<HTMLTableRowElement, TRProps>(
	({ isSelected, children, xcss, testId, ...props }, ref) => {
		const shapeThemeRefresh = fg('charlie-table-shape-refresh');

		return (
			// moving off of Box to support removing some css resets
			<tr
				className={xcss}
				data-testid={testId}
				tabIndex={-1}
				aria-selected={isSelected}
				ref={ref}
				css={[shapeThemeRefresh && lastRowStyle]}
				{...props}
			>
				{children}
			</tr>
		);
	},
);
